import { makeAutoObservable, runInAction } from 'mobx';

import {
  referralLinksService,
  type ReferralLinkCreateInput,
  type ReferralLinkUpdateInput,
} from '@/services/referralLinks';
import {
  REFERRAL_LINK_KIND,
  REFERRAL_LINK_SOURCE,
  type ReferralLink,
} from '@/types/referral';

class ReferralLinksStore {
  links: ReferralLink[] = [];

  loaded = false;
  refreshing = false;
  refreshError: string | null = null;

  creating = false;
  createError: string | null = null;

  updating = false;
  updateError: string | null = null;

  archiving = false;
  archiveError: string | null = null;

  /**
   * Remembers the last refresh's `includeArchived` value so post-mutation
   * refetches preserve the caller's view. Without this, archiving a link
   * from the archived view would silently drop the row when the followup
   * refresh defaulted back to active-only.
   */
  private lastIncludeArchived = false;

  /** Tracks an in-flight `ensureLoaded` so concurrent callers share it. */
  private inFlightEnsure: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isInitialLoading(): boolean {
    return this.refreshing && !this.loaded;
  }

  /** Tracked links (person/campaign) that haven't been archived. */
  get activeTrackedLinks(): ReferralLink[] {
    return this.links.filter(
      (l) => l.kind !== REFERRAL_LINK_KIND.Generic && l.archived_at === null,
    );
  }

  /** Soft-archived tracked links — only present when caller fetched with `includeArchived`. */
  get archivedLinks(): ReferralLink[] {
    return this.links.filter((l) => l.archived_at !== null);
  }

  /**
   * First active campaign link tagged with this channel + deal, or null.
   * Used by the share menu to dedupe: if the user already has an
   * Instagram campaign link for SoFi, reuse its token instead of POST'ing
   * a duplicate. The (deal, channel) tuple disambiguates — sharing
   * Instagram on SoFi vs. on Polaris are different links.
   */
  findCampaignByDealAndChannel(dealId: number, channel: string | null): ReferralLink | null {
    return (
      this.activeTrackedLinks.find(
        (l) =>
          l.kind === REFERRAL_LINK_KIND.Campaign &&
          l.deal_id === dealId &&
          (l.default_channel ?? null) === channel &&
          // Source-tagged links (QR) are their own attribution pool. Without
          // this exclusion the deal's "default" share lookup could grab the
          // QR link — every Copy/Share would then count as a QR scan.
          (l.source ?? null) === null,
      ) ?? null
    );
  }

  /** Active QR-tagged link for the deal, or null if none minted yet. */
  findQrLinkForDeal(dealId: number): ReferralLink | null {
    return (
      this.activeTrackedLinks.find(
        (l) => l.deal_id === dealId && l.source === REFERRAL_LINK_SOURCE.Qr,
      ) ?? null
    );
  }

  /**
   * Per `(dealId, channel?)` lock so two concurrent taps (or a Copy +
   * QR fired in quick succession from the Refer tab) share the same
   * in-flight create promise instead of POSTing twice and minting
   * duplicate rows server-side.
   */
  private ensureInFlight: Map<string, Promise<ReferralLink>> = new Map();

  /**
   * Find-or-create the canonical campaign link for `(dealId, channel?)`.
   * Replaces the legacy `genericLink` fallback — every share URL must be
   * anchored to a specific deal, so callers that previously fell back to
   * the user's generic link instead call this with the current deal
   * context. When `channel` is omitted, this returns the "default" share
   * link for the deal (no channel tag) so the Copy/Share-other rows have
   * a stable URL per deal.
   *
   * Awaits `ensureLoaded()` so the local activeTrackedLinks cache is
   * populated before the lookup — without that, the first call on a
   * cold store would always fall through to a network create even when
   * the user already has a matching link in the DB.
   */
  async ensureCampaignLinkForDeal(
    dealId: number,
    channel: string | null = null,
  ): Promise<ReferralLink> {
    await this.ensureLoaded();

    const existing = this.findCampaignByDealAndChannel(dealId, channel);
    if (existing) return existing;

    const key = `${dealId}::${channel ?? ''}`;
    const inFlight = this.ensureInFlight.get(key);
    if (inFlight) return inFlight;

    const input: ReferralLinkCreateInput = {
      kind: REFERRAL_LINK_KIND.Campaign,
      label: channel ?? 'Share link',
      ...(channel ? { default_channel: channel } : {}),
    };
    const promise = this.create(input, dealId).finally(() => {
      runInAction(() => {
        this.ensureInFlight.delete(key);
      });
    });
    runInAction(() => {
      this.ensureInFlight.set(key, promise);
    });
    return promise;
  }

  /**
   * Find-or-create the deal's QR-tagged share link (source='qr'), so QR
   * scans attribute as link_source=qr — required for source-conditioned
   * bonuses (Sept QR week). Reuses a cached link first; otherwise POSTs
   * with `source`, which the server treats as find-or-create under a
   * partial unique index, so re-opening the sheet can never mint a
   * duplicate even across devices. Same in-flight dedup as the campaign
   * helper — the `qr::` key prefix can't collide with `${dealId}::channel`
   * keys because deal ids are numeric.
   */
  async ensureQrLinkForDeal(dealId: number): Promise<ReferralLink> {
    await this.ensureLoaded();

    const existing = this.findQrLinkForDeal(dealId);
    if (existing) return existing;

    const key = `qr::${dealId}`;
    const inFlight = this.ensureInFlight.get(key);
    if (inFlight) return inFlight;

    const input: ReferralLinkCreateInput = {
      kind: REFERRAL_LINK_KIND.Campaign,
      label: 'QR code',
      source: REFERRAL_LINK_SOURCE.Qr,
    };
    const promise = this.create(input, dealId).finally(() => {
      runInAction(() => {
        this.ensureInFlight.delete(key);
      });
    });
    runInAction(() => {
      this.ensureInFlight.set(key, promise);
    });
    return promise;
  }

  /**
   * Find-or-mint the user's single app-invite link (kind: generic).
   * Server enforces one per user; concurrent callers share the in-flight
   * request the same way ensureCampaignLinkForDeal does.
   */
  async ensureGenericLink(): Promise<ReferralLink> {
    await this.ensureLoaded();

    const existing = this.links.find(
      (l) => l.kind === REFERRAL_LINK_KIND.Generic && !l.archived_at,
    );
    if (existing) return existing;

    const key = 'generic';
    const inFlight = this.ensureInFlight.get(key);
    if (inFlight) return inFlight;

    const promise = referralLinksService
      .generic()
      .then((link) => {
        runInAction(() => {
          // Local cache only — the server row already exists. Guard against
          // a racing refresh() having already inserted it.
          if (!this.links.some((l) => l.id === link.id)) {
            this.links = [ link, ...this.links ];
          }
        });
        return link;
      })
      .finally(() => {
        runInAction(() => {
          this.ensureInFlight.delete(key);
        });
      });
    runInAction(() => {
      this.ensureInFlight.set(key, promise);
    });
    return promise;
  }

  /**
   * Lazy load. Concurrent callers share a single in-flight refresh —
   * each `await ensureLoaded()` resolves only after the load completes,
   * never beforehand.
   */
  ensureLoaded(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.inFlightEnsure) return this.inFlightEnsure;
    this.inFlightEnsure = this.refresh()
      .catch(() => {
        // Error captured on store.refreshError.
      })
      .finally(() => {
        runInAction(() => {
          this.inFlightEnsure = null;
        });
      });
    return this.inFlightEnsure;
  }

  async refresh(opts: { includeArchived?: boolean } = {}): Promise<void> {
    const includeArchived = opts.includeArchived ?? this.lastIncludeArchived;
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
      this.lastIncludeArchived = includeArchived;
    });
    try {
      const response = await referralLinksService.list({ includeArchived });
      runInAction(() => {
        this.links = response.referral_links;
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError =
          err instanceof Error ? err.message : 'Failed to load referral links.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
    }
  }

  /**
   * Create a new tracked link, then refetch the list so display order
   * (server pins generic first, then created_at desc) and any
   * server-derived fields stay canonical. Returns the created link
   * for callers that want to share it immediately.
   *
   * The post-mutation refresh runs in its own try/catch — a refresh
   * failure is logged to `refreshError`, not `createError`, so we don't
   * report a successful server-side mutation as a failure on a
   * transient network blip after the write landed.
   */
  async create(input: ReferralLinkCreateInput, deal_id: number): Promise<ReferralLink> {
    runInAction(() => {
      this.creating = true;
      this.createError = null;
    });
    try {
      const created = await referralLinksService.createOne(input, deal_id);
      runInAction(() => {
        this.creating = false;
      });
      await this.refreshAfterMutation();
      return created;
    } catch (err) {
      runInAction(() => {
        this.createError = err instanceof Error ? err.message : 'Failed to create link.';
        this.creating = false;
      });
      throw err;
    }
  }

  /**
   * Batch create. Used by the contacts/import flow to atomically create
   * N person-links from a device-contact-picker session. Atomic: one
   * invalid item rolls back ALL.
   */
  async createMany(inputs: ReferralLinkCreateInput[], deal_id: number): Promise<ReferralLink[]> {
    runInAction(() => {
      this.creating = true;
      this.createError = null;
    });
    try {
      const response = await referralLinksService.create({ links: inputs, deal_id });
      runInAction(() => {
        this.creating = false;
      });
      await this.refreshAfterMutation();
      return response.referral_links;
    } catch (err) {
      runInAction(() => {
        this.createError = err instanceof Error ? err.message : 'Failed to create links.';
        this.creating = false;
      });
      throw err;
    }
  }

  async update(id: number, input: ReferralLinkUpdateInput): Promise<ReferralLink> {
    runInAction(() => {
      this.updating = true;
      this.updateError = null;
    });
    try {
      const updated = await referralLinksService.update(id, input);
      runInAction(() => {
        this.updating = false;
      });
      await this.refreshAfterMutation();
      return updated;
    } catch (err) {
      runInAction(() => {
        this.updateError = err instanceof Error ? err.message : 'Failed to update link.';
        this.updating = false;
      });
      throw err;
    }
  }

  async archive(id: number): Promise<ReferralLink> {
    runInAction(() => {
      this.archiving = true;
      this.archiveError = null;
    });
    try {
      const archived = await referralLinksService.archive(id);
      runInAction(() => {
        this.archiving = false;
      });
      await this.refreshAfterMutation();
      return archived;
    } catch (err) {
      runInAction(() => {
        this.archiveError = err instanceof Error ? err.message : 'Failed to archive link.';
        this.archiving = false;
      });
      throw err;
    }
  }

  /**
   * Post-mutation refresh. The mutation has already succeeded server-side
   * by the time this runs, so a refresh failure should NOT be reported as
   * a mutation failure — the server state is correct, the local cache is
   * just stale. Errors are surfaced on `refreshError`; the caller's
   * mutation result is preserved.
   */
  private async refreshAfterMutation(): Promise<void> {
    try {
      await this.refresh();
    } catch {
      // refreshError is already set by refresh(); swallow so the
      // successful mutation isn't masked.
    }
  }

  reset(): void {
    runInAction(() => {
      this.links = [];
      this.loaded = false;
      this.refreshing = false;
      this.refreshError = null;
      this.creating = false;
      this.createError = null;
      this.updating = false;
      this.updateError = null;
      this.archiving = false;
      this.archiveError = null;
      this.lastIncludeArchived = false;
    });
  }
}

export const referralLinksStore = new ReferralLinksStore();
// `ReferralLinksStore` class is exported for tests so each test gets a fresh
// instance. Production code should use the `referralLinksStore` singleton.
export { ReferralLinksStore };
